(function () {
  const ACCESS_KEY = 'juju_private_collection_access';
  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const ROOT_SELECTOR = '[data-private-access-root]';
  const CONFIG_SELECTOR = '[data-private-access-config]';
  const GATE_SELECTOR = '[data-private-access-gate]';
  const PROTECTED_SELECTOR = '[data-private-access-protected]';
  const FORM_SELECTOR = '[data-private-access-form]';
  const INPUT_SELECTOR = '[data-private-access-input]';
  const ERROR_SELECTOR = '[data-private-access-error]';
  const SUBMIT_SELECTOR = '[data-private-access-submit]';
  const LOGOUT_SELECTOR = '[data-private-access-logout]';
  const UNAVAILABLE_SELECTOR = '[data-private-access-unavailable]';
  const PRIVATE_COLLECTION_SECTION_SELECTOR = '[data-private-collection-section]';
  const PRIVATE_COLLECTION_PRODUCT_SELECTOR = '[data-private-collection-product-item]';
  const PRIVATE_COLLECTION_DESKTOP_INSERT_SELECTOR = '[data-private-collection-desktop-insert]';
  const PRIVATE_COLLECTION_MOBILE_INSERT_SELECTOR = '[data-private-collection-mobile-insert]';
  const PRIVATE_COLLECTION_PAGINATION_SELECTOR = '[data-private-collection-pagination]';
  const PRIVATE_COLLECTION_LOAD_MORE_SELECTOR = '[data-private-collection-load-more]';
  const PRIVATE_COLLECTION_LOAD_MORE_LABEL_SELECTOR = '[data-private-collection-load-more-label]';
  const privateCollectionMobileQuery = window.matchMedia('(max-width: 1024px)');
  const privateCollectionStates = new WeakMap();

  function readStoredAccess() {
    try {
      return window.localStorage.getItem(ACCESS_KEY);
    } catch (error) {
      return null;
    }
  }

  function writeStoredAccess(value) {
    try {
      window.localStorage.setItem(ACCESS_KEY, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function removeStoredAccess() {
    try {
      window.localStorage.removeItem(ACCESS_KEY);
    } catch (error) {
      return undefined;
    }

    return undefined;
  }

  function parseBoolean(value) {
    return String(value) === 'true';
  }

  function parseDurationDays(value) {
    const parsedValue = Number.parseInt(value, 10);

    if (!Number.isFinite(parsedValue) || parsedValue < 1) {
      return 7;
    }

    return parsedValue;
  }

  function parseInteger(value, fallback = 0) {
    const parsedValue = Number.parseInt(value, 10);

    if (!Number.isFinite(parsedValue)) {
      return fallback;
    }

    return parsedValue;
  }

  function parseMinimumInteger(value, minimum, fallback) {
    const parsedValue = parseInteger(value, fallback);

    if (parsedValue < minimum) {
      return minimum;
    }

    return parsedValue;
  }

  function normalizePasscode(value) {
    return String(value || '').trim().toLocaleLowerCase();
  }

  function setRootState(root, state) {
    root.classList.remove('private-access--pending', 'private-access--granted', 'private-access--denied');
    root.classList.add(`private-access--${state}`);
  }

  function hideError(root) {
    const errorElement = root.querySelector(ERROR_SELECTOR);

    if (!errorElement) {
      return;
    }

    errorElement.hidden = true;
  }

  function showError(root, message) {
    const errorElement = root.querySelector(ERROR_SELECTOR);

    if (!errorElement) {
      return;
    }

    errorElement.textContent = message;
    errorElement.hidden = false;
  }

  function hideUnavailableMessages(root) {
    root.querySelectorAll(UNAVAILABLE_SELECTOR).forEach((messageElement) => {
      messageElement.hidden = true;
    });
  }

  function showUnavailable(root, message) {
    const unavailableMessages = root.querySelectorAll(UNAVAILABLE_SELECTOR);

    hideError(root);
    showGate(root);

    if (unavailableMessages.length > 0) {
      unavailableMessages.forEach((messageElement) => {
        if (message) {
          messageElement.textContent = message;
        }

        messageElement.hidden = false;
      });
    } else if (message) {
      showError(root, message);
    }

    setRootState(root, 'denied');
  }

  function getConfig(root) {
    const configElement = root.querySelector(CONFIG_SELECTOR);

    if (!configElement) {
      return null;
    }

    return {
      mode: configElement.dataset.privateMode || 'gate',
      passcode: configElement.dataset.privatePasscode || '',
      durationDays: parseDurationDays(configElement.dataset.privateDurationDays),
      destination: configElement.dataset.privateDestination || '',
      configured: parseBoolean(configElement.dataset.privateConfigured),
      designMode: parseBoolean(configElement.dataset.privateDesignMode),
      showUnlockedInEditor: parseBoolean(configElement.dataset.privateShowUnlockedInEditor),
    };
  }

  function getSafeReturnUrl() {
    const params = new URLSearchParams(window.location.search);
    const rawReturnUrl = params.get('return_url');

    if (!rawReturnUrl) {
      return null;
    }

    const returnUrl = rawReturnUrl.trim();

    if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return null;
    }

    return returnUrl;
  }

  function getCurrentUrl() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  function getSafeDestinationUrl(rawDestination) {
    if (!rawDestination) {
      return null;
    }

    const destination = rawDestination.trim();

    if (!destination || destination.startsWith('//')) {
      return null;
    }

    if (destination.startsWith('/')) {
      return destination;
    }

    try {
      const url = new URL(destination, window.location.origin);

      if (url.origin !== window.location.origin) {
        return null;
      }

      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return null;
    }
  }

  function buildRedirectUrl(destination, returnUrl) {
    const safeDestination = getSafeDestinationUrl(destination);

    if (!safeDestination) {
      return null;
    }

    try {
      const url = new URL(safeDestination, window.location.origin);

      if (returnUrl) {
        url.searchParams.set('return_url', returnUrl);
      }

      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return safeDestination;
    }
  }

  function isResetRequested() {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset_access') === 'true';
  }

  function isDevelopmentHost() {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  function maybeResetAccess(config) {
    if (!isResetRequested()) {
      return;
    }

    if (config.designMode || isDevelopmentHost()) {
      PrivateAccess.clear();
    }
  }

  function showGate(root) {
    const gateElement = root.querySelector(GATE_SELECTOR);
    const protectedContent = root.querySelector(PROTECTED_SELECTOR);

    if (gateElement) {
      gateElement.hidden = false;
    }

    if (protectedContent) {
      protectedContent.hidden = true;
    }
  }

  function revealProtectedContent(root) {
    const gateElement = root.querySelector(GATE_SELECTOR);
    const protectedContent = root.querySelector(PROTECTED_SELECTOR);

    hideError(root);
    hideUnavailableMessages(root);

    if (gateElement) {
      gateElement.hidden = true;
    }

    if (protectedContent) {
      protectedContent.hidden = false;
    }

    setRootState(root, 'granted');
    syncPrivateCollectionSections(root);
  }

  function bindLogout(root) {
    const logoutButton = root.querySelector(LOGOUT_SELECTOR);

    if (!logoutButton || logoutButton.dataset.privateAccessBound === 'true') {
      return;
    }

    logoutButton.dataset.privateAccessBound = 'true';

    logoutButton.addEventListener('click', (event) => {
      const form = root.querySelector(FORM_SELECTOR);
      const input = root.querySelector(INPUT_SELECTOR);
      const submitButton = root.querySelector(SUBMIT_SELECTOR);

      event.preventDefault();
      PrivateAccess.clear();
      hideUnavailableMessages(root);
      hideError(root);

      if (form) {
        form.reset();
        form.removeAttribute('aria-busy');
      }

      if (submitButton) {
        submitButton.disabled = false;
      }

      showGate(root);
      setRootState(root, 'pending');
      syncPrivateCollectionSections(root);

      if (window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          if (input) {
            input.focus();
          }
        });
      } else if (input) {
        input.focus();
      }
    });
  }

  function bindGateForm(root, config) {
    const form = root.querySelector(FORM_SELECTOR);
    const input = root.querySelector(INPUT_SELECTOR);
    const submitButton = root.querySelector(SUBMIT_SELECTOR);

    if (!form || !input || !submitButton || form.dataset.privateAccessBound === 'true') {
      return;
    }

    form.dataset.privateAccessBound = 'true';

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (submitButton.disabled) {
        return;
      }

      hideError(root);
      hideUnavailableMessages(root);
      submitButton.disabled = true;
      form.setAttribute('aria-busy', 'true');

      const enteredValue = normalizePasscode(input.value);
      const expectedPasscode = normalizePasscode(config.passcode);

      if (!expectedPasscode) {
        showUnavailable(root, 'Private collection access is currently unavailable.');

        window.setTimeout(() => {
          submitButton.disabled = false;
          form.removeAttribute('aria-busy');
          input.focus();
        }, 150);

        return;
      }

      if (enteredValue !== expectedPasscode) {
        showGate(root);
        setRootState(root, 'denied');
        showError(root, 'The passcode entered is incorrect.');

        window.setTimeout(() => {
          submitButton.disabled = false;
          form.removeAttribute('aria-busy');
          input.focus();
          input.select();
        }, 150);

        return;
      }

      if (!PrivateAccess.grant(config.durationDays)) {
        showGate(root);
        setRootState(root, 'denied');
        showError(root, 'Private collection access is currently unavailable.');

        window.setTimeout(() => {
          submitButton.disabled = false;
          form.removeAttribute('aria-busy');
          input.focus();
          input.select();
        }, 150);

        return;
      }

      const safeReturnUrl = getSafeReturnUrl();

      if (safeReturnUrl && safeReturnUrl !== getCurrentUrl()) {
        window.location.replace(safeReturnUrl);
        return;
      }

      revealProtectedContent(root);
      form.removeAttribute('aria-busy');
    });
  }

  function processGateMode(root, config) {
    if (config.designMode) {
      if (config.showUnlockedInEditor) {
        revealProtectedContent(root);
      } else if (config.configured) {
        hideUnavailableMessages(root);
        hideError(root);
        showGate(root);
        setRootState(root, 'pending');
        bindGateForm(root, config);
      } else {
        showUnavailable(root, 'Private collection access is currently unavailable.');
      }

      return;
    }

    if (!config.configured) {
      showUnavailable(root, 'Private collection access is currently unavailable.');
      return;
    }

    if (PrivateAccess.isValid()) {
      const safeReturnUrl = getSafeReturnUrl();

      if (safeReturnUrl && safeReturnUrl !== getCurrentUrl()) {
        window.location.replace(safeReturnUrl);
        return;
      }

      revealProtectedContent(root);
      return;
    }

    hideUnavailableMessages(root);
    hideError(root);
    showGate(root);
    setRootState(root, 'pending');
    bindGateForm(root, config);
  }

  function processRedirectMode(root, config) {
    if (config.designMode) {
      revealProtectedContent(root);
      return;
    }

    if (PrivateAccess.isValid()) {
      revealProtectedContent(root);
      return;
    }

    const redirectUrl = buildRedirectUrl(config.destination, getCurrentUrl());

    if (redirectUrl && redirectUrl !== getCurrentUrl()) {
      window.location.replace(redirectUrl);
      return;
    }

    showUnavailable(root, 'Private collection access is currently unavailable.');
  }

  function getPrivateCollectionSections(scope = document) {
    if (!(scope instanceof Document) && !(scope instanceof HTMLElement)) {
      return [];
    }

    if (scope instanceof HTMLElement && scope.matches(PRIVATE_COLLECTION_SECTION_SELECTOR)) {
      return [scope];
    }

    return Array.from(scope.querySelectorAll(PRIVATE_COLLECTION_SECTION_SELECTOR));
  }

  function getPrivateCollectionState(section) {
    let state = privateCollectionStates.get(section);

    if (!state) {
      state = {
        visiblePageCount: 0,
        visibleProductCount: 0,
      };

      privateCollectionStates.set(section, state);
    }

    return state;
  }

  function getPrivateCollectionProductItems(section) {
    if (!(section instanceof HTMLElement)) {
      return [];
    }

    return Array.from(section.querySelectorAll(PRIVATE_COLLECTION_PRODUCT_SELECTOR));
  }

  function getPrivateCollectionDesktopInserts(section) {
    if (!(section instanceof HTMLElement)) {
      return [];
    }

    return Array.from(section.querySelectorAll(PRIVATE_COLLECTION_DESKTOP_INSERT_SELECTOR));
  }

  function getPrivateCollectionMobileInserts(section) {
    if (!(section instanceof HTMLElement)) {
      return [];
    }

    return Array.from(section.querySelectorAll(PRIVATE_COLLECTION_MOBILE_INSERT_SELECTOR));
  }

  function getPrivateCollectionPagination(section) {
    return section?.querySelector(PRIVATE_COLLECTION_PAGINATION_SELECTOR) || null;
  }

  function getPrivateCollectionLoadMoreButton(section) {
    return section?.querySelector(PRIVATE_COLLECTION_LOAD_MORE_SELECTOR) || null;
  }

  function getPrivateCollectionLoadMoreLabel(section) {
    return section?.querySelector(PRIVATE_COLLECTION_LOAD_MORE_LABEL_SELECTOR) || null;
  }

  function getPrivateCollectionPageSize(section) {
    if (!(section instanceof HTMLElement)) {
      return privateCollectionMobileQuery.matches ? 8 : 9;
    }

    const mobilePageSize = parseMinimumInteger(section.dataset.mobilePageSize, 1, 8);
    const desktopPageSize = parseMinimumInteger(section.dataset.desktopPageSize, 1, 9);

    return privateCollectionMobileQuery.matches ? mobilePageSize : desktopPageSize;
  }

  function getPrivateCollectionRowsPerPage(section) {
    if (!(section instanceof HTMLElement)) {
      return privateCollectionMobileQuery.matches ? 4 : 3;
    }

    const mobileRowsPerPage = parseMinimumInteger(section.dataset.mobileRowsPerPage, 1, 4);
    const desktopRowsPerPage = parseMinimumInteger(section.dataset.desktopRowsPerPage, 1, 3);

    return privateCollectionMobileQuery.matches ? mobileRowsPerPage : desktopRowsPerPage;
  }

  function updatePrivateCollectionButtonState(section, hasMore) {
    const pagination = getPrivateCollectionPagination(section);
    const button = getPrivateCollectionLoadMoreButton(section);
    const label = getPrivateCollectionLoadMoreLabel(section);

    if (pagination instanceof HTMLElement) {
      pagination.hidden = !hasMore;
    }

    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    button.classList.remove('is-loading');
    button.classList.toggle('is-disabled', !hasMore);
    button.disabled = !hasMore;

    if (label instanceof HTMLElement) {
      label.textContent = 'View More';
    }
  }

  function renderPrivateCollectionSection(section, preserveVisibleCount = false) {
    if (!(section instanceof HTMLElement)) {
      return;
    }

    const productItems = getPrivateCollectionProductItems(section);
    const desktopInserts = getPrivateCollectionDesktopInserts(section);
    const mobileInserts = getPrivateCollectionMobileInserts(section);
    const pageSize = getPrivateCollectionPageSize(section);
    const rowsPerPage = getPrivateCollectionRowsPerPage(section);
    const totalProducts = productItems.length;
    const hasItems = totalProducts > 0 || desktopInserts.length > 0 || mobileInserts.length > 0;
    const minimumVisibleProductCount = totalProducts > 0 ? Math.min(pageSize, totalProducts) : 0;
    const state = getPrivateCollectionState(section);
    const isMobile = privateCollectionMobileQuery.matches;
    const showAllInEditor = section.dataset.privateShowAllInEditor === 'true';

    if (showAllInEditor) {
      productItems.forEach((item) => {
        if (item instanceof HTMLElement) {
          item.hidden = false;
        }
      });

      desktopInserts.forEach((item) => {
        if (item instanceof HTMLElement) {
          item.hidden = isMobile;
        }
      });

      mobileInserts.forEach((item) => {
        if (item instanceof HTMLElement) {
          item.hidden = !isMobile;
        }
      });

      updatePrivateCollectionButtonState(section, false);
      return;
    }

    if (!preserveVisibleCount || !state.visiblePageCount) {
      state.visiblePageCount = hasItems ? 1 : 0;
      state.visibleProductCount = minimumVisibleProductCount;
    } else {
      state.visiblePageCount = Math.max(state.visiblePageCount, hasItems ? 1 : 0);
      state.visibleProductCount = Math.min(
        totalProducts,
        Math.max(state.visibleProductCount, minimumVisibleProductCount)
      );
    }

    const viewportPageCount = totalProducts > 0 ? Math.ceil(state.visibleProductCount / pageSize) : 0;
    const activePageCount = hasItems ? Math.max(state.visiblePageCount, viewportPageCount, 1) : 0;
    const allProductsVisible = totalProducts > 0 && state.visibleProductCount >= totalProducts;
    const visibleRowEnd = allProductsVisible ? Number.POSITIVE_INFINITY : activePageCount * rowsPerPage;

    productItems.forEach((item, index) => {
      if (!(item instanceof HTMLElement)) {
        return;
      }

      const productIndex = parseMinimumInteger(item.dataset.privateProductIndex, 1, index + 1);

      item.hidden = productIndex > state.visibleProductCount;
    });

    desktopInserts.forEach((item, index) => {
      if (!(item instanceof HTMLElement)) {
        return;
      }

      const rowStart = parseMinimumInteger(item.dataset.privateDesktopRowStart, 1, index + 1);

      item.hidden = isMobile || rowStart > visibleRowEnd;
    });

    mobileInserts.forEach((item, index) => {
      if (!(item instanceof HTMLElement)) {
        return;
      }

      const rowStart = parseMinimumInteger(item.dataset.privateMobileRowStart, 1, index + 1);

      item.hidden = !isMobile || rowStart > visibleRowEnd;
    });

    let hasMore = false;

    if (isMobile) {
      hasMore = state.visibleProductCount < totalProducts
        || mobileInserts.some((item, index) => {
          if (!(item instanceof HTMLElement)) {
            return false;
          }

          const rowStart = parseMinimumInteger(item.dataset.privateMobileRowStart, 1, index + 1);

          return rowStart > visibleRowEnd;
        });
    } else {
      hasMore = state.visibleProductCount < totalProducts
        || desktopInserts.some((item, index) => {
          if (!(item instanceof HTMLElement)) {
            return false;
          }

          const rowStart = parseMinimumInteger(item.dataset.privateDesktopRowStart, 1, index + 1);

          return rowStart > visibleRowEnd;
        });
    }

    updatePrivateCollectionButtonState(section, hasMore);
  }

  function syncPrivateCollectionSections(scope = document, preserveVisibleCount = false) {
    getPrivateCollectionSections(scope).forEach((section) => {
      renderPrivateCollectionSection(section, preserveVisibleCount);
    });
  }

  function revealFirstNewDesktopInsert(section, previouslyVisibleInserts, previousViewportBottom) {
    if (!(section instanceof HTMLElement) || privateCollectionMobileQuery.matches) {
      return;
    }

    const firstNewInsert = getPrivateCollectionDesktopInserts(section).find((item) => {
      return item instanceof HTMLElement && !item.hidden && !previouslyVisibleInserts.has(item);
    });

    if (!(firstNewInsert instanceof HTMLElement)) {
      return;
    }

    const insertTop = window.scrollY + firstNewInsert.getBoundingClientRect().top;

    if (insertTop >= previousViewportBottom - 24) {
      return;
    }

    const revealOffset = Math.min(window.innerHeight * 0.24, 240);

    window.scrollTo({
      top: Math.max(0, insertTop - revealOffset),
      behavior: 'smooth'
    });
  }

  function handlePrivateCollectionLoadMoreClick(event) {
    const clickTarget = event.target;

    if (!(clickTarget instanceof Element)) {
      return;
    }

    const button = clickTarget.closest(PRIVATE_COLLECTION_LOAD_MORE_SELECTOR);

    if (!(button instanceof HTMLButtonElement) || button.classList.contains('is-loading')) {
      return;
    }

    const section = button.closest(PRIVATE_COLLECTION_SECTION_SELECTOR);

    if (!(section instanceof HTMLElement)) {
      return;
    }

    const label = getPrivateCollectionLoadMoreLabel(section);
    const pageSize = getPrivateCollectionPageSize(section);
    const totalProducts = getPrivateCollectionProductItems(section).length;
    const state = getPrivateCollectionState(section);
    const previouslyVisibleDesktopInserts = new Set(
      getPrivateCollectionDesktopInserts(section).filter((item) => item instanceof HTMLElement && !item.hidden)
    );
    const previousViewportBottom = window.scrollY + window.innerHeight;

    button.classList.add('is-loading');

    if (label instanceof HTMLElement) {
      label.textContent = 'Loading...';
    }

    window.setTimeout(() => {
      state.visiblePageCount = Math.max(state.visiblePageCount, 1) + 1;
      state.visibleProductCount = Math.min(totalProducts, state.visibleProductCount + pageSize);
      renderPrivateCollectionSection(section, true);
      revealFirstNewDesktopInsert(section, previouslyVisibleDesktopInserts, previousViewportBottom);
    }, 140);
  }

  function initializeRoot(root) {
    const config = getConfig(root);

    if (!config) {
      return;
    }

    maybeResetAccess(config);
    bindLogout(root);

    if (config.mode === 'redirect') {
      processRedirectMode(root, config);
      return;
    }

    processGateMode(root, config);
  }

  function getRoots(scope) {
    if (!scope) {
      return Array.from(document.querySelectorAll(ROOT_SELECTOR));
    }

    if (scope.matches && scope.matches(ROOT_SELECTOR)) {
      return [scope];
    }

    return Array.from(scope.querySelectorAll(ROOT_SELECTOR));
  }

  function init(scope) {
    getRoots(scope).forEach((root) => {
      initializeRoot(root);
    });

    syncPrivateCollectionSections(scope);
  }

  const PrivateAccess = {
    key: ACCESS_KEY,
    grant(durationDays) {
      const parsedDurationDays = parseDurationDays(durationDays);
      const now = Date.now();

      return writeStoredAccess({
        grantedAt: now,
        expiresAt: now + (parsedDurationDays * DAY_IN_MS),
      });
    },
    isValid() {
      const storedValue = readStoredAccess();

      if (!storedValue) {
        return false;
      }

      try {
        const access = JSON.parse(storedValue);

        if (
          typeof access !== 'object' ||
          access === null ||
          typeof access.expiresAt !== 'number' ||
          Date.now() >= access.expiresAt
        ) {
          this.clear();
          return false;
        }

        return true;
      } catch (error) {
        this.clear();
        return false;
      }
    },
    clear() {
      removeStoredAccess();
    },
    init,
    mount: initializeRoot,
  };

  window.PrivateAccess = PrivateAccess;

  document.addEventListener('click', handlePrivateCollectionLoadMoreClick);

  init();

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  if (privateCollectionMobileQuery.addEventListener) {
    privateCollectionMobileQuery.addEventListener('change', () => {
      syncPrivateCollectionSections(document, true);
    });
  } else {
    privateCollectionMobileQuery.addListener(() => {
      syncPrivateCollectionSections(document, true);
    });
  }
})();